/**
 * YoMA™ conversation transport + thread persistence.
 *
 * Two things live here so the dashboard panel can't drift from the contract
 * the `ai-coach` function actually expects:
 *
 *  1. Message shape. The function calls `convertToModelMessages`, which needs
 *     AI-SDK `UIMessage`s (`{ id, role, parts: [{type:'text', text}] }`). A
 *     flat `{role, content}` message crashes it server-side, so the panel must
 *     never build the body by hand.
 *  2. Context + history persistence. Every request carries the *latest*
 *     dashboard context (dreams, Life Wheel, roadmap progress) plus the
 *     previous turns, and the thread is stored in cloud state so closing the
 *     panel — or opening it on another device — resumes the same conversation.
 */
import { cloudStorage } from "@/lib/cloudStore";
import { recordOps } from "@/lib/observability";

export type YomaTurn = { role: "user" | "assistant"; content: string };

/** AI-SDK UIMessage, narrowed to the text-only shape YoMA uses. */
export type YomaUiMessage = {
  id: string;
  role: "user" | "assistant";
  parts: { type: "text"; text: string }[];
};

/** Cloud-state key — `wcc360.` is a synced (not device-only) prefix. */
export const YOMA_THREAD_KEY = "wcc360.yoma.thread.v1";

/** How much history travels with each request, newest last. */
const HISTORY_TURNS = 12;
/** How much of the thread we keep at rest. */
const KEEP_TURNS = 40;

export function toUiMessages(turns: YomaTurn[]): YomaUiMessage[] {
  return turns.slice(-HISTORY_TURNS).map((t, i) => ({
    id: `m${i}`,
    role: t.role,
    parts: [{ type: "text", text: t.content }],
  }));
}

export function loadYomaThread(): YomaTurn[] {
  try {
    const raw = cloudStorage.getItem(YOMA_THREAD_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((t): t is YomaTurn =>
        !!t && (t.role === "user" || t.role === "assistant") && typeof t.content === "string")
      .slice(-KEEP_TURNS);
  } catch {
    return [];
  }
}

export function saveYomaThread(turns: YomaTurn[]): void {
  try {
    cloudStorage.setItem(YOMA_THREAD_KEY, JSON.stringify(turns.slice(-KEEP_TURNS)));
  } catch { /* a full quota must not break the conversation */ }
}

export function clearYomaThread(): void {
  try { cloudStorage.removeItem(YOMA_THREAD_KEY); } catch { /* ignore */ }
}

/**
 * Post the thread to `ai-coach` and stream the reply, calling `onDelta` with
 * the accumulated text so the panel can render it as it arrives. Returns the
 * full reply (empty string means the model sent nothing).
 */
export async function streamYomaReply(opts: {
  url: string;
  token: string;
  context: string;
  turns: YomaTurn[];
  onDelta?: (full: string) => void;
  fetchImpl?: typeof fetch;
}): Promise<string> {
  const doFetch = opts.fetchImpl ?? fetch;
  // Monitoring: a YoMA regression is silent (a 500 reads as a generic toast, a
  // stalled stream as "thinking"), so every call is measured — status class,
  // total stream duration, and whether any text actually arrived.
  const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
  const elapsed = () =>
    Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - startedAt);
  const report = (ok: boolean, extra: Record<string, unknown> = {}, err?: unknown) => {
    recordOps({
      area: "ai",
      name: "ai_coach.stream",
      ok,
      duration_ms: elapsed(),
      error_code: err ? (err as { status?: number }).status
        ? `http_${(err as { status?: number }).status}`
        : "stream_error"
        : null,
      error_message: err ? String((err as Error)?.message ?? err).slice(0, 300) : null,
      props: extra,
    });
  };

  try {
    const res = await doFetch(opts.url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${opts.token}`,
      },
      body: JSON.stringify({
        context: opts.context,
        messages: toUiMessages(opts.turns),
      }),
    });
    if (!res.ok || !res.body) {
      report(false, { status: res.status }, { status: res.status, message: `ai-coach ${res.status}` });
      throw new Error(`ai-coach ${res.status}`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    let reply = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split("\n");
      buf = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.startsWith("data:")) continue;
        const payload = line.slice(5).trim();
        if (!payload || payload === "[DONE]") continue;
        try {
          const evt = JSON.parse(payload);
          const delta = evt.delta ?? evt.textDelta ?? (evt.type === "text" ? evt.text : "");
          if (typeof delta === "string" && delta) {
            reply += delta;
            opts.onDelta?.(reply);
          }
        } catch { /* keep-alive / non-JSON frame */ }
      }
    }
    // An empty reply is a 200 that helped nobody — count it as a failure.
    report(reply.length > 0, { status: res.status, chars: reply.length },
      reply.length === 0 ? { message: "empty stream" } : undefined);
    return reply;
  } catch (err) {
    if (!(err instanceof Error && /^ai-coach \d+$/.test(err.message))) report(false, {}, err);
    throw err;
  }
}
